/*
 *   Copyright (c) 2021 Busy Human LLC
 *   All rights reserved.
 *   This file, its contents, concepts, methods, behavior, and operation  (collectively the "Software") are protected by trade secret, patent,  and copyright laws. The use of the Software is governed by a license  agreement. Disclosure of the Software to third parties, in any form,  in whole or in part, is expressly prohibited except as authorized by the license agreement.
 */
import { Globals } from "./globals.js";
import { cloneDeep as _cloneDeep } from "lodash-es";
import { DocumentEntity } from "./document-entity.js";
import { SubsetEntity } from "./subset-entity.js";
import { DefaultDocumentUpdateOptions, DefaultSubsetConstructorOptions } from "../common/firebase/entity-interfaces.js";
import { isObjectEmpty } from "../common/core-data/index.js";
import { DocumentRecycler } from "../common/firebase/document-recycler.js";
import { resolvePath } from "../common/firebase/firebase-paths.js";
import { assertNotNullish, assertType } from "../common/util/assertions.js";
import { assertId, assertPath, assertPathParts } from "../common/firebase/firebase-assertions.js";
import { collection, doc as fbDoc, deleteDoc as delDoc, setDoc, addDoc, getDoc, doc } from "firebase/firestore";
import { SubsetQuery } from "./subset-query.js";
export class CollectionEntity {
    // eslint-disable-next-line no-use-before-define
    static globalInstance = null;
    path;
    recycler;
    constructor({ path }) {
        this.recycler = new DocumentRecycler(this);
        assertPath(path);
        this.path = path;
    }
    firestoreConverter = {
        toFirestore(modelObject) {
            return modelObject;
        },
        fromFirestore(snapshot) {
            return snapshot.data();
        }
    };
    /**
     *
     * @param id - Document ID to grab
     * @param name - Name of the relationship on the document for the collection
     * @returns
     */
    subCollectionEntity(id, name) {
        assertId(id, this.path);
        assertNotNullish(name, "name should not be nullish");
        if (!this.relationships)
            throw new Error(`No relationships defined`);
        const doc = this.docEntity(id);
        return doc.subCollectionEntity(name.toString());
    }
    subPath(...pathParts) {
        assertPathParts(...pathParts);
        return resolvePath(this.path, ...pathParts);
    }
    get id() {
        return this.ref().id;
    }
    ref() {
        return collection(Globals.getFirestore(), this.path).withConverter(this.firestoreConverter);
    }
    setReference(ref) {
        this.path = ref.path;
    }
    docRef(id) {
        return fbDoc(Globals.getFirestore(), this.path, id).withConverter(this.firestoreConverter);
    }
    docEntity(id) {
        assertId(id, this.path);
        return this.recycler.reuseOrGenerate(id, (id2) => {
            const ref = this.docRef(id2);
            return new DocumentEntity(this, ref, this.defaults());
        });
    }
    async updateDoc(id, data, options = DefaultDocumentUpdateOptions()) {
        assertId(id, this.path);
        const doc = await this.fetchDoc(id);
        await doc.update(data, options);
        return doc;
    }
    async fetchData(id) {
        assertId(id, this.path);
        const doc = await this.fetchDoc(id);
        return doc.data({ clone: true });
    }
    deleteDoc(id) {
        assertId(id, this.path);
        return delDoc(this.docRef(id));
    }
    async add(data, options = {}) {
        let resolvedData;
        if (!data || isObjectEmpty(data)) {
            resolvedData = this.defaults();
        }
        else {
            resolvedData = _cloneDeep(Object.assign(this.defaults(), data));
        }
        if (options.id) {
            assertId(options.id, this.path);
            const docRef = fbDoc(Globals.getFirestore(), this.path, options.id).withConverter(this.firestoreConverter);
            await setDoc(docRef, resolvedData);
            return this.recycler.reuseOrGenerate(docRef.id, () => new DocumentEntity(this, docRef, resolvedData));
        }
        else {
            const ref = await addDoc(this.ref(), resolvedData);
            return this.recycler.reuseOrGenerate(ref.id, () => new DocumentEntity(this, ref, resolvedData));
        }
    }
    async fetchDoc(id, options = {}) {
        assertId(id, this.path);
        const docSnap = await getDoc(this.docRef(id));
        if (docSnap.exists() && !isObjectEmpty(docSnap.data())) {
            return this.recycler.updateOrGenerate(docSnap, s => new DocumentEntity(this, s.ref, s.data()));
        }
        else if (options.dataIfMissing) {
            return this.add(options.dataIfMissing, { id });
        }
        else if (options.defaultIfMissing) {
            return this.add({}, { id });
        }
        else {
            throw new Error(`Collection ${this.path}; No document found: ${id}`);
        }
    }
    all(options) {
        return this.subset(undefined, options);
    }
    async fetchAll(options) {
        const all = this.all({ ...options, collection: this });
        await all.fetch();
        return all;
    }
    async fetchDocWithQuery(queryFn) {
        const subset = this.subset(queryFn);
        await subset.fetch();
        if (subset.size === 0) {
            return null;
        }
        if (subset.size > 1) {
            console.warn(`fetchDocWithQuery expected 0 or 1 matches but got: ${subset.size}`);
            console.log("Actual Items: ", subset.items);
        }
        return subset.items[0];
    }
    query(queryFn) {
        let ref;
        if (typeof queryFn !== 'undefined') {
            ref = queryFn(this.ref(), new SubsetQuery(this.ref()));
        }
        else {
            ref = this.ref();
        }
        return ref;
    }
    subset(queryFn, options) {
        if (typeof queryFn !== 'undefined')
            assertType(queryFn, "function", "CollectionEntity.subset(queryfn...), queryFn");
        options = options || DefaultSubsetConstructorOptions();
        options.collection = this;
        const query = this.query(queryFn);
        assertNotNullish(query, "CollectionEntity.subset(queryfn...), queryFn");
        return new SubsetEntity(query, options);
    }
    // static emptySubset<Type>() {
    //     return new SubsetEntity<Type>(null);
    // }
    // static emptyDocument<Type extends object>() {
    //     return new DocumentEntity<Type>(this);
    // }
    emptyDocument() {
        return new DocumentEntity(this);
    }
    cleanup() {
        // No-op for now
    }
    makeId() {
        return doc(this.ref()).id;
    }
}
//# sourceMappingURL=collection-entity.js.map